import { AsyncPipe } from '@angular/common'
import { CommonModule } from '@angular/common'
import {
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    signal,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import {
    AuthStateService,
    UserOrganizationStateService,
} from '@baboor/baboor-auth'
import { SubscriptionStateService } from '@baboor/baboor-subscription'
import { ProfileStateService } from '@baboor/baboor-user'
import { AuthApiService, Organization } from '@baboor/common-auth'
import {
    GroupListItemsComponent,
    UserLoginStatusComponent,
} from '@baboor/common-components'
import { TextSlicePipe } from '@baboor/common-pipes'
import { AlertService } from '@baboor/common-services'
import { WA_LOCATION } from '@ng-web-apis/common'
import { DynamicDialogModule } from '@openng/optimus-ui/dynamicdialog'
import {
    DialogService,
    DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog'
import { TooltipModule } from '@openng/optimus-ui/tooltip'
import { filter } from 'rxjs'
import { getClientPageTitle } from '../../../client-navigation.data'
import { ClientProfileDropdownComponent } from '../../../client-ui/client-profile-dropdown/client-profile-dropdown.component'
import { HeaderUtilService } from '../../header-utils/header-util.service'
import { UIstate } from '../../header-utils/ui-state-interface'
import { HeaderQuickSettingsComponent } from '../header-quick-settings/header-quick-settings.component'

@Component({
    selector: 'app-header-client-default',
    imports: [
        RouterModule,
        TextSlicePipe,
        UserLoginStatusComponent,
        AsyncPipe,
        GroupListItemsComponent,
        ClientProfileDropdownComponent,
        DynamicDialogModule,
        CommonModule,
        TooltipModule,
        HeaderQuickSettingsComponent,
    ],
    templateUrl: './header-client-default.component.html',
    styleUrl: './header-client-default.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DialogService, DynamicDialogRef],
})
export class HeaderClientDefaultComponent implements OnInit, OnDestroy {
    protected subscriptionStateService = inject(SubscriptionStateService)
    private alertService = inject(AlertService)
    private router = inject(Router)
    protected userGroupStateService = inject(UserOrganizationStateService)
    protected renderer: Renderer2 = inject(Renderer2)
    protected headerUtilService = inject(HeaderUtilService)
    protected authStateService = inject(AuthStateService)
    protected authApiService = inject(AuthApiService)
    protected profileStateService = inject(ProfileStateService)
    private readonly location = inject(WA_LOCATION)
    private destroyRef = inject(DestroyRef)

    groupType = signal<string>('')
    pageTitle = signal<string>('Home')
    @Input() notificationCount = 0
    @Output() sidebarToggle = new EventEmitter<void>()
    @Output() notificationsClick = new EventEmitter<void>()
    @ViewChild('dropdown') dropdown?: ElementRef
    @ViewChild('orgDropdown') orgDropdown?: ElementRef
    @ViewChild('orgChevron') orgChevron?: ElementRef

    groups = signal<any>([])

    isVisible = signal<boolean>(false)
    isLoading = signal<boolean>(false)
    isError = signal<boolean>(false)
    selectedGroupId = signal<string | null>(null)
    organization = signal<Organization | null>(null)
    isOrgDropdownVisible = signal<boolean>(false)
    uiState: UIstate = {
        imageLoaded: true,
        showProfileDropDown: false,
    }
    subscriptionPlanName = signal<string>('')
    subscriptionPlanId = signal<string>('')
    subscriptionType = signal<string>('')

    bodyClickListener: (() => void) | null = null

    constructor() {
        this.pageTitle.set(getClientPageTitle(this.router.url))
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.pageTitle.set(getClientPageTitle(this.router.url))
            })
    }

    toggleSidebar() {
        this.sidebarToggle.emit()
    }

    toggleOrgDropdown() {
        this.isOrgDropdownVisible.set(!this.isOrgDropdownVisible())
        if (this.orgChevron) {
            const chevronEl = this.orgChevron.nativeElement
            if (chevronEl.classList.contains('rotate-90')) {
                this.renderer.removeClass(chevronEl, 'rotate-90')
            } else {
                this.renderer.addClass(chevronEl, 'rotate-90')
            }
        }
    }

    showFallback(event: Event) {
        this.headerUtilService.showFallbackText(event, this.uiState)
    }

    ngOnInit() {
        this.bodyClickListener = this.renderer.listen(
            document.body,
            'click',
            (e: Event) => {
                if (
                    this.dropdown?.nativeElement.contains(e.target) === false &&
                    !(e.target as HTMLElement).closest('user-login-status')
                ) {
                    this.isVisible.set(false)
                }
                if (
                    this.orgDropdown?.nativeElement.contains(e.target) ===
                        false &&
                    !(e.target as HTMLElement).closest('.client-org-selector')
                ) {
                    this.isOrgDropdownVisible.set(false)
                    if (this.orgChevron) {
                        this.renderer.removeClass(
                            this.orgChevron.nativeElement,
                            'rotate-90',
                        )
                    }
                }
            },
        )

        this.subscriptionStateService.init(
            this.authStateService.getOrganizationId() ?? '',
        )

        this.organization.set(this.authStateService.getOrganization() ?? null)
        this.profileStateService.init()
        this.groupType.set(this.authStateService.getGroupType() as string)
        this.userGroupStateService.init(0)
        this.userGroupStateService
            .select('groups')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((groups) => {
                this.groups.set(groups)
            })
    }

    onViewDropDown() {
        this.isVisible.set(!this.isVisible())
    }

    onSwitchGroup(id: string) {
        this.isLoading.set(true)
        this.selectedGroupId.set(id)
        this.authStateService.switchOrg(id).subscribe({
            next: (_res) => {
                this.isLoading.set(false)
                this.isOrgDropdownVisible.set(false)
                this.isVisible.set(false)
                if (this.orgChevron) {
                    this.renderer.removeClass(
                        this.orgChevron.nativeElement,
                        'rotate-90',
                    )
                }
                this.alertService.success('Organization switched successfully.')
                window.location.href = '/dashboard/home'
            },
            error: () => {
                this.isLoading.set(false)
                this.selectedGroupId.set(null)
                this.isError.set(true)
                this.alertService.error('Failed to switch organization')
            },
        })
    }

    onNavigate(string: string) {
        this.router.navigate([string])
        this.isVisible.set(false)
        this.isOrgDropdownVisible.set(false)
        if (this.orgChevron) {
            this.renderer.removeClass(
                this.orgChevron.nativeElement,
                'rotate-90',
            )
        }
    }

    onDropdownClose() {
        this.isVisible.set(false)
    }

    ngOnDestroy() {
        if (this.bodyClickListener) {
            this.bodyClickListener()
            this.bodyClickListener = null
        }
    }

    onUpgradeSubscription(id: string) {
        this.router.navigate(['/dashboard/organization/subscribe-plan', id], {
            queryParams: { isYearly: this.subscriptionType() },
        })
    }

    onLoadOwnedGroups(event: {
        wrapper: HTMLDivElement
        chevron: HTMLSpanElement
    }) {
        const { wrapper, chevron } = event
        this.userGroupStateService.loadMyGroups().subscribe({
            next: ({ data }) => {
                const groupCount = data.groups?.length || 0
                const createButtonCount = [
                    data.hasVendorGroup,
                    data.hasClientGroup,
                ].filter((has) => !has).length
                const itemHeight = 40
                const baseHeight =
                    groupCount * itemHeight + createButtonCount * itemHeight
                const calculatedHeight = Math.max(baseHeight, 120)
                wrapper.classList.toggle('h-0')
                chevron.classList.toggle('rotate-90')
                if (wrapper.classList.contains('h-0')) {
                    wrapper.style.height = '0px'
                } else {
                    wrapper.style.height = `${calculatedHeight}px`
                }
            },
            error: (error) => {
                console.error('Failed to load groups:', error)
            },
        })
    }
}
